// ==========================================
// Poolaroo Product Widgets
// Customer-facing JS widgets embedded in product descriptions (calculators,
// fit finders). theme.liquid loads this file on product pages only.
//
// Design standard: poolaroo_web_widgets (owned by the poolaroo-writer
// session; D:\Projects\poolaroo-writer\style-guides\guides\poolaroo_web_widgets.md).
// Built against draft v0.2, 2026-11-02. Ask poolaroo-writer for the latest
// version before changing or adding a widget; don't invent colors or classes.
//
// Conventions:
//  - A product description holds only an empty mount div, e.g.
//    <div id="cove-calculator" class="pw-mount"></div>. Each widget mounts
//    only when its id is on the page. Never rename a live mount id.
//  - All styling comes from the shared #pw-styles block below (tokens + pw-
//    components). custom-article-widgets.js carries an identical copy of
//    PW_CSS: change one, change both.
//  - Every selector sits under .pw-widget; every id is pw-<widget>-<field>.
//  - Cart actions go through PW.addToCart (Shopify Cart AJAX API) after an
//    explicit click, then fire the theme's cart:build + cart:open events so
//    the drawer and header count refresh. Products are looked up by handle at
//    page load, so stock and variant ids are always live.
//  - One `// WIDGET: <name>` block per widget. Append, don't interleave.
// ==========================================

(function() {
  'use strict';

  var PW_CSS = `
.pw-widget {
  --pw-primary: #007ab8;
  --pw-primary-hover: #006FA8;
  --pw-focus: #006FA8;
  --pw-link: #006FA8;
  --pw-brand-blue: #0090D9;
  --pw-brand-gold: #FDB100;
  --pw-brand-red: #EE6055;
  --pw-brand-green: #60D394;
  --pw-text: #000000;
  --pw-text-muted: #4f5b66;
  --pw-surface: #f7f9fb;
  --pw-surface-raised: #ffffff;
  --pw-result-bg: #eaf4fb;
  --pw-border: #d5dde5;
  --pw-control-border: #6b7785;
  --pw-success-text: #1F7561;
  --pw-success-bg: #e8f5ef;
  --pw-success-border: #60D394;
  --pw-warning-text: #000000;
  --pw-warning-bg: #fff3cd;
  --pw-warning-border: #FDB100;
  --pw-error-text: #B42318;
  --pw-error-bg: #fdecea;
  --pw-error-border: #EE6055;
  --pw-font-body: inherit;
  --pw-font-heading: var(--typeHeaderPrimary), var(--typeHeaderFallback);
  --pw-size-title: 20px;
  --pw-size-result: 28px;
  --pw-size-body: 16px;
  --pw-size-label: 15px;
  --pw-size-small: 14px;
  --pw-size-input: 16px;
  --pw-weight-body: inherit;
  --pw-weight-strong: 700;
  --pw-weight-heading: 600;
  --pw-leading-body: 1.5;
  --pw-leading-tight: 1.25;
  --pw-space-1: 4px;
  --pw-space-2: 8px;
  --pw-space-3: 12px;
  --pw-space-4: 16px;
  --pw-space-5: 24px;
  --pw-space-6: 32px;
  --pw-radius-card: 6px;
  --pw-radius-control: 3px;
  --pw-border-width: 1px;
  --pw-accent-width: 5px;
  --pw-focus-width: 2px;
  --pw-shadow: none;
  --pw-max-width: 600px;
  --pw-touch: 44px;

  box-sizing: border-box; width: 100%; max-width: var(--pw-max-width);
  margin: var(--pw-space-5) 0; padding: var(--pw-space-5);
  background: var(--pw-surface); border: var(--pw-border-width) solid var(--pw-border);
  border-radius: var(--pw-radius-card); box-shadow: var(--pw-shadow);
  color: var(--pw-text); font-family: var(--pw-font-body); font-weight: var(--pw-weight-body);
  font-size: var(--pw-size-body); line-height: var(--pw-leading-body); text-align: left;
}
.pw-widget *, .pw-widget *::before, .pw-widget *::after { box-sizing: inherit; }
.pw-widget [hidden] { display: none !important; }
.pw-widget p, .pw-widget ul, .pw-widget ol, .pw-widget table { margin: 0; }
.pw-widget a:not(.btn) { color: var(--pw-link); }

.pw-widget .pw-title {
  font-family: var(--pw-font-heading); font-weight: var(--pw-weight-heading);
  font-size: var(--pw-size-title); line-height: var(--pw-leading-tight);
  color: var(--pw-text); margin: 0 0 var(--pw-space-2); text-transform: none; letter-spacing: normal;
}
.pw-widget .pw-subtitle { color: var(--pw-text-muted); margin: 0 0 var(--pw-space-4); }

.pw-widget .pw-field { margin: 0 0 var(--pw-space-4); }
.pw-widget .pw-label {
  display: block; font-size: var(--pw-size-label); font-weight: var(--pw-weight-strong);
  line-height: var(--pw-leading-tight); margin: 0 0 var(--pw-space-1); color: var(--pw-text); padding: 0;
}
/* theme.css styles every label as small uppercase with letter-spacing, via
   label:not(.kit-radio):not(.text-label) (specificity 0,2,1). The doubled
   .pw-widget outranks it, so labels keep the guide's sentence case and sizes. */
.pw-widget.pw-widget label.pw-label, .pw-widget.pw-widget label.pw-choice__option {
  font-family: inherit; text-transform: none; letter-spacing: normal;
}
.pw-widget.pw-widget label.pw-label { font-size: var(--pw-size-label); }
.pw-widget.pw-widget label.pw-choice__option { font-size: var(--pw-size-body); }
.pw-widget .pw-help { font-size: var(--pw-size-small); color: var(--pw-text-muted); margin: 0 0 var(--pw-space-2); }
.pw-widget .pw-input, .pw-widget .pw-select {
  display: block; width: 100%; min-height: var(--pw-touch); margin: 0;
  padding: var(--pw-space-2) var(--pw-space-3);
  font-family: inherit; font-size: var(--pw-size-input); line-height: var(--pw-leading-tight);
  color: var(--pw-text); background: var(--pw-surface-raised);
  border: var(--pw-border-width) solid var(--pw-control-border); border-radius: var(--pw-radius-control);
}
.pw-widget .pw-select:disabled { background: var(--pw-surface); color: var(--pw-text-muted); cursor: not-allowed; }
.pw-widget .pw-input[aria-invalid="true"] { border: 2px solid var(--pw-error-border); }
.pw-widget .pw-error-text {
  font-size: var(--pw-size-small); font-weight: var(--pw-weight-strong);
  color: var(--pw-error-text); margin: var(--pw-space-1) 0 0;
}
.pw-widget .pw-row { display: flex; flex-wrap: wrap; gap: 0 var(--pw-space-4); }
.pw-widget .pw-row > .pw-field { flex: 1 1 200px; }

.pw-widget .pw-choice { border: 0; padding: 0; margin: 0 0 var(--pw-space-4); min-width: 0; }
.pw-widget .pw-choice__options { display: flex; flex-wrap: wrap; gap: var(--pw-space-2); }
.pw-widget .pw-choice__option {
  display: flex; align-items: center; gap: var(--pw-space-2); flex: 1 1 0;
  min-height: var(--pw-touch); margin: 0; padding: var(--pw-space-2) var(--pw-space-3);
  background: var(--pw-surface-raised); border: var(--pw-border-width) solid var(--pw-control-border);
  border-radius: var(--pw-radius-control); cursor: pointer; font-size: var(--pw-size-body); font-weight: var(--pw-weight-body);
}
.pw-widget .pw-choice__option input { width: 18px; height: 18px; margin: 0; flex-shrink: 0; accent-color: var(--pw-primary); }
.pw-widget .pw-choice__option.is-checked {
  border: 2px solid var(--pw-primary); background: var(--pw-result-bg); font-weight: var(--pw-weight-strong);
}

.pw-widget .pw-btn { min-height: var(--pw-touch); margin: 0; }
.pw-widget .pw-btn[disabled] { cursor: not-allowed; opacity: 0.6; }
.pw-widget .pw-btn:focus-visible, .pw-widget .pw-input:focus-visible, .pw-widget .pw-select:focus-visible,
.pw-widget .pw-choice__option input:focus-visible, .pw-widget a:focus-visible,
.pw-widget .pw-table-scroll:focus-visible, .pw-widget .pw-result:focus-visible {
  outline: var(--pw-focus-width) solid var(--pw-focus); outline-offset: 2px;
}

.pw-widget .pw-result {
  background: var(--pw-result-bg); border-left: var(--pw-accent-width) solid var(--pw-primary);
  border-radius: var(--pw-radius-control); padding: var(--pw-space-4); margin: var(--pw-space-4) 0 0;
}
.pw-widget .pw-result__label {
  font-size: var(--pw-size-small); font-weight: var(--pw-weight-strong); color: var(--pw-text-muted);
  margin: 0 0 var(--pw-space-1);
}
.pw-widget .pw-result__value {
  font-family: var(--pw-font-heading); font-size: var(--pw-size-result); font-weight: var(--pw-weight-heading);
  line-height: var(--pw-leading-tight); color: var(--pw-text); margin: 0 0 var(--pw-space-2);
}
.pw-widget .pw-result__value--text { font-size: var(--pw-size-title); }
.pw-widget .pw-result__unit { font-size: var(--pw-size-body); }
.pw-widget .pw-result__detail { margin: 0 0 var(--pw-space-3); }
.pw-widget .pw-result__detail:last-child { margin-bottom: 0; }
.pw-widget .pw-result__heading { font-weight: var(--pw-weight-strong); margin: var(--pw-space-3) 0 var(--pw-space-1); }
.pw-widget .pw-result__next { margin: var(--pw-space-3) 0 0; }
.pw-widget .pw-result--success { background: var(--pw-success-bg); border-left-color: var(--pw-success-border); }
.pw-widget .pw-result--success .pw-result__label { color: var(--pw-success-text); }
.pw-widget .pw-result--warning { background: var(--pw-warning-bg); border-left-color: var(--pw-warning-border); }
.pw-widget .pw-result--warning .pw-result__label { color: var(--pw-warning-text); }
.pw-widget .pw-result--error { background: var(--pw-error-bg); border-left-color: var(--pw-error-border); }
.pw-widget .pw-result--error .pw-result__label { color: var(--pw-error-text); }
.pw-widget .pw-list { padding-left: var(--pw-space-5); margin: 0 0 var(--pw-space-3); }
.pw-widget .pw-list li { margin: 0 0 var(--pw-space-1); }

.pw-widget .pw-table-scroll { overflow-x: auto; margin: 0 0 var(--pw-space-3); }
.pw-widget .pw-table {
  width: 100%; border-collapse: collapse; table-layout: auto;
  background: var(--pw-surface-raised); font-size: var(--pw-size-body);
}
.pw-widget .pw-table th, .pw-widget .pw-table td {
  border: var(--pw-border-width) solid var(--pw-border); padding: var(--pw-space-2) var(--pw-space-3);
  text-align: left; vertical-align: top;
}
.pw-widget .pw-table thead th { background: var(--pw-surface); font-weight: var(--pw-weight-strong); }
.pw-widget .pw-table tbody th { font-weight: var(--pw-weight-strong); }
.pw-widget .pw-table .pw-num { text-align: right; }

.pw-widget .pw-options { display: grid; grid-template-columns: 1fr; gap: var(--pw-space-3); margin: var(--pw-space-3) 0 0; }
.pw-widget .pw-option {
  background: var(--pw-surface-raised); border: var(--pw-border-width) solid var(--pw-border);
  border-radius: var(--pw-radius-card); padding: var(--pw-space-4);
}
.pw-widget .pw-option__name { font-weight: var(--pw-weight-strong); margin: 0 0 var(--pw-space-1); }
.pw-widget .pw-option__sku {
  font-family: var(--pw-font-heading); font-size: var(--pw-size-title); font-weight: var(--pw-weight-heading);
  line-height: var(--pw-leading-tight); margin: 0 0 var(--pw-space-2);
}
.pw-widget .pw-option__sku a, .pw-widget .pw-table th a { text-decoration: underline; text-underline-offset: 0.15em; }
.pw-widget .pw-option__desc { font-size: var(--pw-size-small); color: var(--pw-text-muted); margin: 0 0 var(--pw-space-3); }
.pw-widget .pw-option__price { font-weight: var(--pw-weight-strong); margin: 0 0 var(--pw-space-2); }

.pw-widget .pw-note { font-size: var(--pw-size-small); color: var(--pw-text-muted); margin: var(--pw-space-3) 0 0; }
.pw-widget .pw-warning {
  background: var(--pw-warning-bg); border-left: var(--pw-accent-width) solid var(--pw-warning-border);
  border-radius: var(--pw-radius-control); padding: var(--pw-space-3) var(--pw-space-4);
  margin: var(--pw-space-4) 0 0; color: var(--pw-warning-text);
}
.pw-widget .pw-alert {
  background: var(--pw-error-bg); border-left: var(--pw-accent-width) solid var(--pw-error-border);
  border-radius: var(--pw-radius-control); padding: var(--pw-space-3) var(--pw-space-4);
  margin: var(--pw-space-4) 0 0;
}
.pw-widget .pw-status { font-size: var(--pw-size-small); margin: var(--pw-space-2) 0 0; }
.pw-widget .pw-status:empty { display: none; }
.pw-widget .pw-status--ok { color: var(--pw-success-text); font-weight: var(--pw-weight-strong); }
.pw-widget .pw-status--error { color: var(--pw-error-text); font-weight: var(--pw-weight-strong); }
.pw-widget .pw-visually-hidden {
  position: absolute !important; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}

@media (min-width: 769px) {
  .pw-widget .pw-input--short { max-width: 280px; }
  .pw-widget .pw-options--2 { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 768px) {
  .pw-widget { padding: var(--pw-space-4); }
  .pw-widget .pw-btn { width: 100%; }
  .pw-widget .pw-choice__options { flex-direction: column; }
}
@media (prefers-reduced-motion: no-preference) {
  .pw-widget .pw-result { animation: pw-fade 150ms ease-out; }
}
@keyframes pw-fade { from { opacity: 0; } to { opacity: 1; } }
`;

  var CONTACT_URL = 'https://poolaroo.com/pages/contact';
  var TIMEOUT_MS = 10000;

  var PW = {};

  PW.injectStyles = function() {
    if (document.getElementById('pw-styles')) return;
    var s = document.createElement('style');
    s.id = 'pw-styles';
    s.textContent = PW_CSS;
    document.head.appendChild(s);
  };

  // Mount once per id. A widget that throws leaves an empty mount, so the
  // description around it still reads correctly.
  PW.mount = function(id, build) {
    function run() {
      var el = document.getElementById(id);
      if (!el || el.getAttribute('data-pw-mounted')) return;
      el.setAttribute('data-pw-mounted', '1');
      PW.injectStyles();
      try { build(el); PW.adoptHeading(el); } catch (e) { console.error('[pw] ' + id, e); el.innerHTML = ''; }
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
  };

  // Guide v0.6 §4.2: when the description puts an h2, h3 or h4 directly above
  // the mount, that heading labels the widget and the widget drops its own
  // title, so there's only one heading above it. The heading gets the
  // title's id (pw-<widget>-title) if it has none.
  PW.adoptHeading = function(el) {
    var h = el.previousElementSibling;
    var card = el.querySelector('.pw-widget');
    var title = card && card.querySelector('.pw-title');
    if (!h || !/^H[234]$/.test(h.tagName) || !title) return;
    var id = title.id;
    title.parentNode.removeChild(title);
    if (!h.id) h.id = id;
    card.setAttribute('aria-labelledby', h.id);
  };

  PW.esc = function(s) {
    return String(s).replace(/[&<>"']/g, function(c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  PW.fmt = function(n, maxDecimals) {
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: maxDecimals || 0 });
  };

  // "$250" for whole dollars, "$249.97" otherwise (brand voice 01 §6).
  PW.money = function(cents) {
    var whole = cents % 100 === 0;
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 });
  };

  PW.plural = function(n, one, many) { return n === 1 ? one : many; };

  PW.track = function(tool, type, value) {
    if (typeof window.sendAnalyticsEvent === 'function') window.sendAnalyticsEvent(tool, type, value);
  };

  PW.debounce = function(fn, ms) {
    var t;
    return function() { clearTimeout(t); t = setTimeout(fn, ms); };
  };

  PW.root = function() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  };

  PW.fetchWithTimeout = function(url, options) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function() { if (ctrl) ctrl.abort(); }, TIMEOUT_MS);
    var opts = options || {};
    if (ctrl) opts.signal = ctrl.signal;
    return fetch(url, opts).finally(function() { clearTimeout(timer); });
  };

  // Product JSON by handle (variants with id, available, price). Resolves null
  // when the product isn't sold online (404: missing, draft or unpublished).
  var productCache = {};
  PW.getProduct = function(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = PW.fetchWithTimeout(PW.root() + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { 'Accept': 'application/json' }
      }).then(function(r) {
        if (r.status === 404) return null;
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
      productCache[handle].catch(function() { delete productCache[handle]; });
    }
    return productCache[handle];
  };

  PW.firstVariant = function(product) {
    return product && product.variants && product.variants.length ? product.variants[0] : null;
  };

  PW.contactLink = function() {
    return '<a href="' + CONTACT_URL + '">Contact our team</a>';
  };

  PW.productUrl = function(handle) {
    return PW.root() + 'products/' + encodeURIComponent(handle);
  };

  PW.productLink = function(handle, text) {
    return '<a href="' + PW.productUrl(handle) + '">' + PW.esc(text) + '</a>';
  };

  // True on that product's own page, where a link to it would go nowhere.
  PW.isCurrentProduct = function(handle) {
    return location.pathname.replace(/\/$/, '').split('/').pop() === handle;
  };

  // Guide §4.10: out of stock means no button, a plain statement and a link
  // to the product page.
  PW.outOfStockHtml = function(name, handle) {
    return '<p class="pw-status">' + PW.esc(name) + ' is out of stock right now. See ' +
      PW.productLink(handle, 'its product page') + ' for details.</p>';
  };

  // Adds items ([{ id, quantity }]) to the cart after a click, then refreshes
  // and opens the theme's cart drawer. opts: { tool, successText, pageLinks }
  // where pageLinks is the product-page link HTML offered when the add fails.
  PW.addToCart = function(items, button, statusEl, opts) {
    opts = opts || {};
    var label = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding to cart...';
    statusEl.className = 'pw-status';
    statusEl.textContent = '';

    return PW.fetchWithTimeout(PW.root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function(r) {
      return r.json().catch(function() { return {}; }).then(function(data) {
        if (!r.ok) {
          var err = new Error(data.description || data.message || ('HTTP ' + r.status));
          err.shopifyMessage = data.description || data.message || '';
          throw err;
        }
        return data;
      });
    }).then(function() {
      statusEl.className = 'pw-status pw-status--ok';
      var hasDrawer = !!document.getElementById('CartDrawer');
      statusEl.innerHTML = PW.esc(opts.successText || 'Added to your cart.') +
        (hasDrawer ? '' : ' <a href="' + PW.root() + 'cart">View your cart</a>');
      document.dispatchEvent(new CustomEvent('cart:build'));
      if (hasDrawer) document.dispatchEvent(new CustomEvent('cart:open'));
      if (opts.tool) PW.track(opts.tool, 'Add_To_Cart', items.map(function(i) { return i.id + 'x' + i.quantity; }).join(','));
    }).catch(function(e) {
      // Guide §4.10: a failed add shows the widget error state, never silence.
      statusEl.className = 'pw-alert';
      var reason = e && e.shopifyMessage ? PW.esc(e.shopifyMessage) : 'We couldn\'t add that to your cart.';
      statusEl.innerHTML = 'Error: ' + reason + ' Try again, or add it from the product page' +
        (opts.pageLinks ? ': ' + opts.pageLinks + '.' : '.');
      console.error('[pw] cart add failed', e);
    }).finally(function() {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = label;
    });
  };

  // ---- Form helpers ----

  // Accepts "20,000", " 24.5 ". Returns NaN for anything else.
  PW.parseNumber = function(raw) {
    var s = String(raw).replace(/[,\s]/g, '');
    return /^\d*\.?\d+$/.test(s) ? parseFloat(s) : NaN;
  };

  // spec: { id, label, help, min, max, integer, short }
  PW.numberField = function(spec) {
    var helpId = spec.id + '-help';
    var errId = spec.id + '-error';
    return '<div class="pw-field">' +
      '<label class="pw-label" for="' + spec.id + '">' + spec.label + '</label>' +
      (spec.help ? '<p class="pw-help" id="' + helpId + '">' + spec.help + '</p>' : '') +
      '<input class="pw-input' + (spec.short === false ? '' : ' pw-input--short') + '" id="' + spec.id + '" type="text" ' +
        'inputmode="' + (spec.integer ? 'numeric' : 'decimal') + '" autocomplete="off"' +
        (spec.help ? ' aria-describedby="' + helpId + '"' : '') + '>' +
      '<p class="pw-error-text" id="' + errId + '" hidden></p>' +
    '</div>';
  };

  // Reads a number field against its spec. { empty } | { error } | { value }
  PW.readNumber = function(input, spec) {
    if (input.value.trim() === '') return { empty: true };
    var v = PW.parseNumber(input.value);
    if (isNaN(v) || v < spec.min || v > spec.max || (spec.integer && v % 1 !== 0)) {
      return { error: 'Enter a ' + (spec.integer ? 'whole number' : 'number') + ' from ' + PW.fmt(spec.min) + ' to ' + PW.fmt(spec.max) + '.' };
    }
    return { value: v };
  };

  PW.setError = function(input, message) {
    var errEl = document.getElementById(input.id + '-error');
    var helpId = input.id + '-help';
    var described = document.getElementById(helpId) ? helpId : '';
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      errEl.textContent = 'Error: ' + message;
      errEl.hidden = false;
      input.setAttribute('aria-describedby', (described ? described + ' ' : '') + errEl.id);
    } else {
      input.removeAttribute('aria-invalid');
      errEl.textContent = '';
      errEl.hidden = true;
      if (described) input.setAttribute('aria-describedby', described); else input.removeAttribute('aria-describedby');
    }
  };

  // Wires a number field: live recompute on input, error shown on blur.
  PW.wireNumber = function(input, spec, onChange) {
    var debounced = PW.debounce(onChange, 300);
    input.addEventListener('input', function() {
      var r = PW.readNumber(input, spec);
      if (r.value !== undefined || r.empty) PW.setError(input, '');
      debounced();
    });
    input.addEventListener('blur', function() {
      var r = PW.readNumber(input, spec);
      PW.setError(input, r.error || '');
    });
  };

  // options: [{ value, label }]
  PW.choiceGroup = function(name, legend, options) {
    return '<fieldset class="pw-choice"><legend class="pw-label">' + legend + '</legend><div class="pw-choice__options">' +
      options.map(function(o) {
        return '<label class="pw-choice__option"><input type="radio" name="' + name + '" value="' + o.value + '"> ' + o.label + '</label>';
      }).join('') + '</div></fieldset>';
  };

  PW.wireChoice = function(root, name, onChange) {
    var radios = root.querySelectorAll('input[name="' + name + '"]');
    function sync() {
      radios.forEach(function(r) { r.parentNode.classList.toggle('is-checked', r.checked); });
    }
    radios.forEach(function(r) { r.addEventListener('change', function() { sync(); onChange(r.value); }); });
    sync();
  };

  PW.choiceValue = function(root, name) {
    var c = root.querySelector('input[name="' + name + '"]:checked');
    return c ? c.value : '';
  };

  PW.card = function(widget, titleText, subtitle, body) {
    return '<section class="pw-widget pw-widget--' + widget + '" aria-labelledby="pw-' + widget + '-title">' +
      '<h3 class="pw-title" id="pw-' + widget + '-title">' + titleText + '</h3>' +
      (subtitle ? '<p class="pw-subtitle">' + subtitle + '</p>' : '') +
      body + '</section>';
  };

  // Option card for a product the person can add: name, SKU, description,
  // live price and an add button, or "not sold online" with a contact link.
  // The SKU links to the product page once the lookup finds it (Dieter,
  // 2026-11-02), except on that product's own page.
  // opt: { name, sku, desc, handle, buttonLabel, successText }.
  // Fills `slot` once the product lookup returns.
  PW.renderProductOption = function(slot, opt, trackTool) {
    slot.innerHTML = '<p class="pw-option__name">' + PW.esc(opt.name) + '</p>' +
      '<p class="pw-option__sku">' + PW.esc(opt.sku) + '</p>' +
      (opt.desc ? '<p class="pw-option__desc">' + PW.esc(opt.desc) + '</p>' : '') +
      '<div class="pw-option__buy" aria-busy="true"><p class="pw-status">Checking stock...</p></div>';
    var buy = slot.querySelector('.pw-option__buy');
    PW.getProduct(opt.handle).then(function(product) {
      buy.removeAttribute('aria-busy');
      var v = PW.firstVariant(product);
      if (product && !PW.isCurrentProduct(opt.handle)) {
        slot.querySelector('.pw-option__sku').innerHTML = '<a href="' + PW.productUrl(opt.handle) + '" aria-label="' +
          PW.esc(opt.sku + ' product page') + '">' + PW.esc(opt.sku) + '</a>';
      }
      if (!v) {
        buy.innerHTML = '<p class="pw-status">Not sold online right now. ' + PW.contactLink() + ' and we\'ll check it for you.</p>';
        return;
      }
      if (!v.available) {
        buy.innerHTML = PW.outOfStockHtml(opt.sku, opt.handle);
        return;
      }
      buy.innerHTML = '<p class="pw-option__price">' + PW.money(v.price) + '</p>' +
        '<button type="button" class="btn pw-btn">' + PW.esc(opt.buttonLabel) + '</button>' +
        '<p class="pw-status" role="status" aria-live="polite"></p>';
      var btn = buy.querySelector('button');
      btn.addEventListener('click', function() {
        PW.addToCart([{ id: v.id, quantity: 1 }], btn, buy.querySelector('[role="status"]'), {
          tool: trackTool, successText: opt.successText, pageLinks: PW.productLink(opt.handle, opt.sku + ' product page')
        });
      });
    }).catch(function() {
      buy.removeAttribute('aria-busy');
      buy.innerHTML = '<p class="pw-status pw-status--error">Error: We couldn\'t check stock. Refresh the page, or ' +
        PW.contactLink().replace('Contact', 'contact') + ' and we\'ll check it for you.</p>';
    });
  };

  window.PoolarooWidgets = PW;

  // ==========================================
  // WIDGET: Fiber Calculator (Jack's Magic Filter Fiber Stuff)
  // Mount: #jacks-fiber-calculator-target (live on JMFIBER09/18/36; keep the id)
  // First built 2026-04-10; rebuilt to poolaroo_web_widgets v0.2 on 2026-11-02.
  //
  // Formula source: the "Product Dosage" section of the Fiber Stuff product
  // description on poolaroo.com (checked 2026-11-02): DE filters 1 DE scoop
  // per 12 sq ft; cartridge filters 3 oz (by weight) per 25 sq ft; sand filters
  // 3 oz at a time, 9 oz maximum. The same page's FAQ says it's added through
  // the skimmer and can replace DE. The 7 oz-by-weight-per-scoop conversion
  // is our stated estimate (Dieter, 2026-11-02): Jack's Magic gives the DE dose
  // in scoops only, and retailer copy uses about 7 oz. The widget says so up
  // front when DE is chosen, with the caveat that a scoop's weight varies.
  //   DE:        oz = ceil(sq ft / 12 x 7)
  //   Cartridge: oz = ceil(sq ft / 25 x 3)
  //   Sand:      9 oz maximum, added 3 oz at a time
  // Bags: largest bags first, the remainder in the smallest in-stock bag
  // (rounded up), then pairs of a size merged into the next size up.
  //
  // Test cases (checked by hand):
  //   DE 60 sq ft         -> 35 oz (5.0 scoops) -> 1 bag (36 oz)
  //   Cartridge 400 sq ft -> 48 oz              -> 1 bag (36 oz) + 1 bag (18 oz)
  //   Cartridge 100 sq ft -> 12 oz              -> 1 bag (18 oz) (two 9-oz bags merge into one 18-oz)
  //   Sand                -> 9 oz               -> 1 bag (9 oz), or 1 bag (18 oz) when the 9-oz is sold out
  // ==========================================
  PW.mount('jacks-fiber-calculator-target', function(mount) {
    var TOOL = 'Jacks Magic Fiber';
    var HANDLE = 'jacks-magic-filter-fiber-stuff-9-oz-bag-jmfiber09';
    // Bag sizes in oz by variant id (one product, three variants).
    var BAGS = [
      { oz: 36, id: 54030505541907 },
      { oz: 18, id: 54030505509139 },
      { oz: 9, id: 54030505574675 }
    ];
    var SIZE = { id: 'pw-fiber-size', label: 'Filter size (square feet)', min: 10, max: 2000 };
    var HELP = {
      de: 'It\'s on the filter\'s label, for example "60 sq ft".',
      cartridge: 'For a filter with several cartridges, add them together. Four 100-sq-ft cartridges make 400 sq ft.'
    };

    mount.innerHTML = PW.card('fiber', 'How much filter fiber do you need?',
      'Choose your filter type and enter its size. We\'ll work out the amount and the bags to buy.',
      '<form class="pw-form" novalidate>' +
        PW.choiceGroup('pw-fiber-type', 'Filter type', [
          { value: 'de', label: 'DE' }, { value: 'cartridge', label: 'Cartridge' }, { value: 'sand', label: 'Sand' }
        ]) +
        '<div id="pw-fiber-size-wrap" hidden>' + PW.numberField(Object.assign({ help: HELP.de }, SIZE)) + '</div>' +
      '</form>' +
      '<p class="pw-note" id="pw-fiber-de-note" hidden>We estimate one DE scoop of fiber at 7 oz by weight. A scoop\'s weight varies with how it\'s filled and packed, so these amounts are estimates only.</p>' +
      // Guide §8 filter-aid row (W34): PPE warning with every result, from the
      // product page's Safety Data Sheet points ("Wear protective gloves &
      // eye/face protection"), checked 2026-11-02.
      '<div class="pw-warning" id="pw-fiber-warning" hidden><strong>Warning:</strong> Wear gloves and eye protection when you add it.</div>' +
      '<div class="pw-result" id="pw-fiber-result" aria-live="polite" hidden></div>' +
      '<p class="pw-note">Estimate only. Follow the dosing directions on the bag.</p>');

    var root = mount.querySelector('.pw-widget');
    var sizeWrap = root.querySelector('#pw-fiber-size-wrap');
    var sizeInput = root.querySelector('#pw-fiber-size');
    var helpEl = root.querySelector('#pw-fiber-size-help');
    var result = root.querySelector('#pw-fiber-result');
    var warning = root.querySelector('#pw-fiber-warning');
    var deNote = root.querySelector('#pw-fiber-de-note');
    var stock = null; // { variantId: available }
    var analyticsTimer;

    root.querySelector('form').addEventListener('submit', function(e) { e.preventDefault(); });

    PW.getProduct(HANDLE).then(function(p) {
      if (!p) return;
      stock = {};
      p.variants.forEach(function(v) { stock[v.id] = v.available; });
      update();
    }).catch(function() { /* unknown stock: offer every size; the cart reports a sold-out bag */ });

    function inStockBags() {
      return BAGS.filter(function(b) { return !stock || stock[b.id] !== false; });
    }

    function packBags(totalOz, bags) {
      var counts = bags.map(function() { return 0; });
      var rem = totalOz;
      for (var i = 0; i < bags.length - 1; i++) {
        counts[i] = Math.floor(rem / bags[i].oz);
        rem = rem % bags[i].oz;
      }
      counts[bags.length - 1] = Math.ceil(rem / bags[bags.length - 1].oz);
      for (var j = bags.length - 1; j > 0; j--) {
        var ratio = bags[j - 1].oz / bags[j].oz;
        if (ratio % 1 === 0 && counts[j] >= ratio) {
          counts[j - 1] += Math.floor(counts[j] / ratio);
          counts[j] = counts[j] % ratio;
        }
      }
      return bags.map(function(b, k) { return { bag: b, count: counts[k] }; }).filter(function(x) { return x.count > 0; });
    }

    function update() {
      var type = PW.choiceValue(root, 'pw-fiber-type');
      clearTimeout(analyticsTimer);
      warning.hidden = true;
      sizeWrap.hidden = !type || type === 'sand';
      deNote.hidden = type !== 'de';
      if (type && type !== 'sand') helpEl.textContent = HELP[type];
      if (!type) { result.hidden = true; return; }

      var totalOz, detail, care, sqft = null;
      if (type === 'sand') {
        totalOz = 9;
        detail = 'Add 3 oz at a time through the skimmer, up to 9 oz in total.';
        care = 'Backwash when the pressure gauge reads 8 to 10 PSI above your clean starting pressure.';
      } else {
        var r = PW.readNumber(sizeInput, SIZE);
        if (r.value === undefined) { result.hidden = true; return; }
        sqft = r.value;
        if (type === 'de') {
          var scoops = sqft / 12;
          totalOz = Math.ceil(scoops * 7);
          detail = 'That\'s about ' + PW.fmt(scoops, 1) + ' DE scoops for a ' + PW.fmt(sqft, 1) + '-sq-ft filter, at one scoop per 12 sq ft and our estimate of 7 oz per scoop, rounded up to the next ounce. It\'s used in place of DE powder.';
          care = 'Backwash and recharge when the pressure gauge reads 8 to 10 PSI above your clean starting pressure.';
        } else {
          totalOz = Math.ceil((sqft / 25) * 3);
          detail = 'That\'s for a ' + PW.fmt(sqft, 1) + '-sq-ft cartridge filter at 3 oz per 25 sq ft, rounded up to the next ounce. It improves the filtering of cartridge filters.';
          care = 'Clean the cartridges when pressure rises 8 to 10 PSI above your starting pressure.';
        }
      }

      var bags = inStockBags();
      var html = '<p class="pw-result__label">You need</p>' +
        '<p class="pw-result__value">' + PW.fmt(totalOz) + ' oz <span class="pw-result__unit">' + (type === 'sand' ? 'at most, by weight' : 'by weight') + '</span></p>' +
        '<p class="pw-result__detail">' + detail + '</p>';

      var addLabel, successText;
      if (!bags.length) {
        html += PW.outOfStockHtml('Fiber Stuff', HANDLE);
      } else {
        var pack = packBags(totalOz, bags);
        var packOz = pack.reduce(function(s, x) { return s + x.count * x.bag.oz; }, 0);
        var bagCount = pack.reduce(function(s, x) { return s + x.count; }, 0);
        // Name the sold-out sizes only when they're why there's more left over.
        var fullStockOz = packBags(totalOz, BAGS).reduce(function(s, x) { return s + x.count * x.bag.oz; }, 0);
        var soldOut = packOz > fullStockOz ? BAGS.filter(function(b) { return bags.indexOf(b) === -1; }) : [];
        // "1 bag (36 oz)", "2 bags (18 oz)" (writer review, 2026-11-02).
        var bagText = function(x) { return x.count + ' ' + PW.plural(x.count, 'bag', 'bags') + ' (' + x.bag.oz + ' oz)'; };
        html += '<p class="pw-result__heading">Bags to buy</p><ul class="pw-list">' +
          pack.map(function(x) { return '<li>' + bagText(x) + '</li>'; }).join('') + '</ul>';
        if (packOz > totalOz) {
          html += '<p class="pw-result__detail">That\'s ' + PW.fmt(packOz) + ' oz, so you\'ll have ' + PW.fmt(packOz - totalOz) + ' oz left over' +
            (soldOut.length ? ' because the ' + soldOut.map(function(b) { return b.oz + '-oz'; }).join(' and ') + ' ' + PW.plural(soldOut.length, 'bag is', 'bags are') + ' out of stock.' : '.') + '</p>';
        }
        // Guide §4.10: one item names its quantity and size; a set is listed
        // above the button and uses "Add all N to cart".
        addLabel = pack.length === 1 ? 'Add ' + bagText(pack[0]) + ' to cart' : 'Add all ' + bagCount + ' to cart';
        successText = 'Added ' + pack.map(bagText).join(' and ') + ' of Fiber Stuff to your cart.';
        html += '<p class="pw-result__next"><button type="button" class="btn pw-btn" id="pw-fiber-add">' + addLabel + '</button></p>' +
          '<p class="pw-status" role="status" aria-live="polite"></p>';
        result.dataset.items = JSON.stringify(pack.map(function(x) { return { id: x.bag.id, quantity: x.count }; }));
      }
      html += '<p class="pw-result__heading">When to replace it</p><p class="pw-result__detail">' + care + '</p>';

      result.innerHTML = html;
      result.hidden = false;
      warning.hidden = false;
      var addBtn = result.querySelector('#pw-fiber-add');
      if (addBtn) {
        addBtn.addEventListener('click', function() {
          PW.addToCart(JSON.parse(result.dataset.items), addBtn, result.querySelector('[role="status"]'), {
            tool: TOOL, successText: successText, pageLinks: PW.productLink(HANDLE, 'Fiber Stuff product page')
          });
        });
      }
      analyticsTimer = setTimeout(function() { PW.track(TOOL, type, sqft === null ? 'N/A' : sqft); }, 2000);
    }

    PW.wireChoice(root, 'pw-fiber-type', update);
    PW.wireNumber(sizeInput, SIZE, update);
  });

  // ==========================================
  // WIDGET: US Seal Pump Shaft Seal Finder
  // Mount: #us-seal-compatibility-target (live on 8 US Seal products; keep the id)
  // Rebuilt to poolaroo_web_widgets v0.2 on 2026-11-02: add to cart replaces
  // the search links.
  // ==========================================
  PW.mount('us-seal-compatibility-target', function(mount) {
    var TOOL = 'US_Seal_Compatibility';
    // Hardcoded fit list: a known deviation from guide §9 (W32). No new
    // hardcoded lists; this one moves to a maintained data source later.
    // Fit data is owned by the Compatibility Transformer project, not this
    // file: apply its corrections, don't edit rows here by judgment.
    // Source: US Seal's Pool & Spa Pump Products Cross-Reference Guide (2021,
    // p2) and its Sta-Rite/SwimQuip seal bulletin (2021), via the audit in
    // Compatibility Transformer exports/us-seal/widget_corrections.json
    // (per-row source page, reason and prior value) plus widget_additions.json
    // (91 rows, each read twice independently from the PDFs). Manufacturers
    // and models are sorted A to Z. Last checked: 2026-11-02.
    // basic: "none" = the guide lists no Basic Service seal for that pump; the
    // finder shows "N/A" in that slot. An empty mfgNo = the guide gives
    // no part number for that model.
    var sealData = {
      "Acura Spa Systems, Inc.": [
        { model: "Aquaheat / Aquaheat Plus", mfgNo: "1090-A", basic: "PS-601", premier: "PS-3986" },
        { model: "Magnaflow / Magnaflow 56", mfgNo: "1090-A", basic: "PS-601", premier: "PS-3986" }
      ],
      "American Products (Pentair)": [
        { model: "Americana", mfgNo: "395005", basic: "PS-201", premier: "PS-3868" },
        { model: "Americana II", mfgNo: "397012", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Bronze-Bronze", mfgNo: "397012", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Bronze-Noryl", mfgNo: "395005", basic: "PS-201", premier: "PS-3868" },
        { model: "Eagle", mfgNo: "397012, 397020", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Maxim", mfgNo: "397012", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Power-Glass", mfgNo: "397012", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Ultra-Flo", mfgNo: "397020", basic: "PS-1000", premier: "PS-3865R" }
      ],
      "Ampro-Diaclear": [
        { model: "Dura-Flo, Noryl Turbo, XL 600", mfgNo: "AP-304D", basic: "PS-1000", premier: "PS-3865R" }
      ],
      "Anthony & Sylvan": [
        { model: "AC, AC Series", mfgNo: "04380", basic: "PS-1000", premier: "PS-3865R" },
        { model: "AJ Series", mfgNo: "04547", basic: "PS-100", premier: "PS-3866" }
      ],
      "Arneson / Astral Pool": [
        { model: "Astramax", mfgNo: "Series 1600", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Pool Sweep", mfgNo: "A-8", basic: "PS-200", premier: "PS-3867" },
        { model: "Sena", mfgNo: "Series 1800", basic: "PS-200", premier: "PS-3867" },
        { model: "Spirit 2000", mfgNo: "Series 1800", basic: "PS-201", premier: "PS-3868" }
      ],
      "Baker-Hydro": [
        { model: "Manufacturer Part Number 35B1175, 35B1178", mfgNo: "35B1175, 35B1178", basic: "PS-200", premier: "PS-3867" }
      ],
      "Doughboy Industries": [
        { model: "Manufacturer Part Number 306-1002", mfgNo: "306-1002", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Manufacturer Part Number 306-1007", mfgNo: "306-1007", basic: "PS-200", premier: "PS-3867" }
      ],
      "Flo Tec (Pentair)": [
        { model: "AT25101, AT251501", mfgNo: "", basic: "PS-201", premier: "PS-3868" },
        { model: "FP6022, FP6042", mfgNo: "", basic: "PS-201", premier: "PS-3868" },
        { model: "FPT20075, 20510, 20515", mfgNo: "", basic: "PS-201", premier: "PS-3868" }
      ],
      "Gecko Alliance (Aqua Flo)": [
        { model: "A & B Series", mfgNo: "92509040", basic: "PS-1000", premier: "PS-3865R" },
        { model: "A-Series", mfgNo: "92500050", basic: "PS-1000", premier: "PS-3865R" },
        { model: "C-Series", mfgNo: "92500050", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Circ-Master", mfgNo: "92500151", basic: "PS-200", premier: "PS-3867" },
        { model: "Circ-Master CP, HP, XP", mfgNo: "", basic: "PS-3960", premier: "PS-3867" },
        { model: "D-Series (92500040)", mfgNo: "92500040", basic: "PS-1000", premier: "PS-3865R" },
        { model: "D-Series (92500050)", mfgNo: "92500050", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Dominator (High Head / Med Head)", mfgNo: "92500150", basic: "PS-200", premier: "PS-3867" },
        { model: "Flo-Master (91049000)", mfgNo: "91049000", basic: "PS-2131", premier: "PS-3869" },
        { model: "Flo-Master (92500150)", mfgNo: "92500150", basic: "PS-200", premier: "PS-3867" },
        { model: "Flo-Master CP/HP", mfgNo: "", basic: "PS-200", premier: "PS-3867" },
        { model: "Flo-Master XP2 / XP2E", mfgNo: "", basic: "PS-200", premier: "PS-3867" },
        { model: "Flo-Master XP3", mfgNo: "", basic: "PS-200", premier: "PS-3867" },
        { model: "Pond-Flo", mfgNo: "92500151", basic: "PS-200", premier: "PS-3867" },
        { model: "Tub-Master", mfgNo: "91049000", basic: "PS-2131", premier: "PS-3869" },
        { model: "Tub-Master FMCP / FMHP / FMVP", mfgNo: "92500150", basic: "PS-200", premier: "PS-3867" }
      ],
      "Hayward Industries": [
        { model: "Booster Pump 5060", mfgNo: "SPX1250XZ2", basic: "PS-200", premier: "PS-3867" },
        { model: "Booster Pump 6060", mfgNo: "SPX2700SA", basic: "PS-3960", premier: "PS-3867" },
        { model: "Cast Iron", mfgNo: "SPX1250XZ2", basic: "PS-200", premier: "PS-3867" },
        { model: "EcoStar, EcoStar SVRS", mfgNo: "SPX3200SA", basic: "PS-3890", premier: "PS-4280" },
        { model: "Manufacturer Part Number SPX1600Z2GV", mfgNo: "SPX1600Z2GV", basic: "none", premier: "PS-201V-CMS" },
        { model: "MaxFlo (SP2800X Series)", mfgNo: "SPX1600Z2", basic: "PS-201", premier: "PS-3868" },
        { model: "MaxFlo II, MaxFloXL", mfgNo: "SPX2700SA", basic: "PS-3960", premier: "PS-3867" },
        { model: "MaxFlo VS 500 Omni", mfgNo: "SPX2700SAV", basic: "none", premier: "PS-3867" },
        { model: "NorthStar", mfgNo: "SPX3200SA", basic: "PS-3890", premier: "PS-4280" },
        { model: "Super Pump", mfgNo: "SPX1600Z2, SPX2605X7A", basic: "PS-201", premier: "PS-3868" },
        { model: "Super Pump II", mfgNo: "SPX2605X71", basic: "PS-201", premier: "PS-3868" },
        { model: "Super Pump VS 700 Omni", mfgNo: "SPX1600Z2VIT", basic: "none", premier: "PS-3868" },
        { model: "TriStar / TriStar VS", mfgNo: "SPX3200SA", basic: "PS-3890", premier: "PS-4280" },
        { model: "TriStar 900 Series", mfgNo: "SPX4000SAV", basic: "none", premier: "PS-4280" }
      ],
      "Jacuzzi": [
        { model: "A, J, JCM Series", mfgNo: "10-0002-06", basic: "PS-200", premier: "PS-3867" },
        { model: "Cygnet", mfgNo: "10-0002-06", basic: "PS-200", premier: "PS-3867" },
        { model: "K, KM Series", mfgNo: "10-0002-06", basic: "PS-200", premier: "PS-3867" },
        { model: "L3 & L25", mfgNo: "", basic: "PS-200", premier: "PS-3867" },
        { model: "LCM, LCU Series, Vector", mfgNo: "10-1436-00", basic: "PS-200", premier: "PS-3867" },
        { model: "LH,LPC,LPH,LR", mfgNo: "10-0002-06", basic: "PS-200", premier: "PS-3867" },
        { model: "Magnum", mfgNo: "10-1502-09", basic: "PS-201", premier: "PS-3868" },
        { model: "Splash Pak K", mfgNo: "", basic: "PS-200", premier: "PS-3867" },
        { model: "Vector (LVL)", mfgNo: "10-0002-06", basic: "PS-200", premier: "PS-3867" }
      ],
      "Jandy (Zodiac)": [
        { model: "ePump Variable Speed", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" },
        { model: "FHP, JEP, MHP, PHP", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" },
        { model: "FloPro (FHPM) Series", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" },
        { model: "Max HP Series", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" },
        { model: "MHPM", mfgNo: "R0445500", basic: "none", premier: "PS-4231" },
        { model: "Plus HP (PHPF/PHPM) Series New Style", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" },
        { model: "Plus HP (PHPF/PHPM) Series Old Style", mfgNo: "R0445500", basic: "none", premier: "PS-4231" },
        { model: "SHP, SWF, WFTR", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" },
        { model: "Stealth (New Style)", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" },
        { model: "Stealth (Old Style)", mfgNo: "R0445500", basic: "none", premier: "PS-4231" },
        { model: "VS FloPro Series, VS Plus HP Series", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" },
        { model: "Water Feature Series", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" }
      ],
      "Marlow": [
        { model: "Argonaut", mfgNo: "38592-00", basic: "PS-201", premier: "PS-3868" },
        { model: "Mardur", mfgNo: "38592-00", basic: "PS-201", premier: "PS-3868" }
      ],
      "Pentair Pool Products": [
        { model: "CF6", mfgNo: "U109-136SS", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Challenger", mfgNo: "354545", basic: "PS-200", premier: "PS-3867" },
        { model: "Challenger Centrifugal", mfgNo: "354545S", basic: "PS-200", premier: "PS-3867" },
        { model: "Dynamo Aboveground", mfgNo: "354545", basic: "PS-200", premier: "PS-3867" },
        { model: "Dynatron", mfgNo: "354545", basic: "PS-200", premier: "PS-3867" },
        { model: "Hydropump", mfgNo: "354001", basic: "PS-201", premier: "PS-3868" },
        { model: "IntelliFlo", mfgNo: "071734S", basic: "PS-961", premier: "PS-3865R" },
        { model: "IntelliFlo 011018", mfgNo: "071734", basic: "PS-1000", premier: "PS-3865R" },
        { model: "IntelliFlo VSF", mfgNo: "071734SP", basic: "PS-1000", premier: "PS-3865R" },
        { model: "IntelliFloXF VSP", mfgNo: "17351-0101S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "L-Series (17351-0102)", mfgNo: "17351-0102", basic: "PS-100", premier: "PS-3866" },
        { model: "L-Series (071728)", mfgNo: "071728", basic: "PS-201", premier: "PS-3868" },
        { model: "Manufacturer Part Number 353253S", mfgNo: "353253S", basic: "PS-200", premier: "PS-3867" },
        { model: "Optiflo Aboveground", mfgNo: "354545S", basic: "PS-200", premier: "PS-3867" },
        { model: "SuperFlo", mfgNo: "354545S", basic: "PS-200", premier: "PS-3867" },
        { model: "SuperFlo VST", mfgNo: "17351-0101S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Universal Booster Pump", mfgNo: "071734", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Waterfall Specialty Pump", mfgNo: "354545S", basic: "PS-200", premier: "PS-3867" },
        { model: "Whisperflo", mfgNo: "071734S", basic: "PS-1000", premier: "PS-3865R" }
      ],
      "Polaris (Zodiac)": [
        { model: "Polaris PB4-60 Booster Pump", mfgNo: "P55", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Polaris PB4SQ Booster Pump", mfgNo: "R0479400", basic: "PS-3890", premier: "PS-4280" }
      ],
      "Speck Industries": [
        { model: "A91, A91-11VSP, BADU Pro Series ES90, ES90-II", mfgNo: "2921606204", basic: "PS-200", premier: "PS-3867" },
        { model: "Manufacturer Part Number 60603-200", mfgNo: "60603-200", basic: "PS-200", premier: "PS-3867" },
        { model: "Manufacturer Part Number 2920060603", mfgNo: "2920060603", basic: "PS-200", premier: "PS-3867" }
      ],
      "Sta-Rite (Pentair)": [
        { model: "CF/CFA Series", mfgNo: "U109-136SS", basic: "PS-1000", premier: "PS-3865R" },
        { model: "DJ/JS/JT Series", mfgNo: "37400-0027S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Dura-Glas (PE, PEA, P2R, P2RA)", mfgNo: "17304-0100S", basic: "PS-200", premier: "PS-3867" },
        { model: "Dura-Glas II", mfgNo: "U109-358SS, U109-372SS", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Dura-Glas II (P4E, P4EA, P4R, P4RA)", mfgNo: "37400-0027S, 37400-0028S", basic: "none", premier: "PS-201V-CMS" },
        { model: "Dura-Jet", mfgNo: "U109-358SS", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Dyna-Glas / Dyna-Max", mfgNo: "U109-358SS, 37400-0027S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Dyna-Jet", mfgNo: "17351-0101S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Dyna-Pro, Dyna-Pro E", mfgNo: "17351-0101S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Dyna-Wave", mfgNo: "17351-0101S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "HP-Series", mfgNo: "U109-136SS", basic: "PS-1000", premier: "PS-3865R" },
        { model: "JS Series", mfgNo: "", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "JW/JWPA Series", mfgNo: "U109-358SS, 37400-0027S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Max-E-Glas (PE, PEA, P2R, P2RA)", mfgNo: "17304-0100S", basic: "PS-200", premier: "PS-3867" },
        { model: "Max-E-Glas II", mfgNo: "U109-358SS, U109-372SS, 37400-0027S", basic: "PS-201", premier: "PS-201V-CMS" },
        { model: "Max-E-Glas II (P4 with 37400-0028S)", mfgNo: "37400-0028S", basic: "none", premier: "PS-201V-CMS" },
        { model: "Max-E-Pro (P6E, P6RA)", mfgNo: "37400-0028S", basic: "none", premier: "PS-201V-CMS" },
        { model: "Max-E-ProXF VS", mfgNo: "17351-0101S", basic: "PS-201", premier: "PS-201V-CMS" }
      ],
      "Sundance Spas": [
        { model: "Manufacturer Part Number 6500-447", mfgNo: "6500-447", basic: "PS-200", premier: "PS-3867" },
        { model: "Theramax / Theraflo", mfgNo: "6500-281", basic: "PS-201", premier: "PS-201V-CMS" }
      ],
      "SwimQuip": [
        { model: "XL, VI/XL, VII Series", mfgNo: "37407-0009", basic: "PS-201", premier: "PS-201V-CMS" }
      ],
      "Waterway Plastics": [
        { model: "Booster Pump", mfgNo: "319-3290", basic: "PS-4577", premier: "PS-4280" },
        { model: "Champion 56-Frame", mfgNo: "319-3100/B, 319-3110", basic: "PS-1000", premier: "PS-3865R" },
        { model: "CSA Hi-Flo II Side Discharge", mfgNo: "319-3100/B, 319-3110", basic: "PS-1000", premier: "PS-3865R" },
        { model: "CSA Supreme, E Series Center Discharge", mfgNo: "319-3010/B 319-3090/B", basic: "none", premier: "PS-3867" },
        { model: "EX2, HI FLO 2", mfgNo: "319-3010/B 319-3090/B", basic: "none", premier: "PS-3867" },
        { model: "Executive - Viton - 56 Frame", mfgNo: "319-3100/B, 319-3110", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Executive 48 & 56 Frame", mfgNo: "319-3100/B, 319-3110", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Hi-Flo Side Discharge", mfgNo: "319-3100/B, 319-3110", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Iron Might (2013-Present)", mfgNo: "319-3010/B, 319-3090/B", basic: "none", premier: "PS-3867" },
        { model: "Iron Might (Pre-2013)", mfgNo: "319-3100/B, 319-3110", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Power Defender (Variations except Booster)", mfgNo: "319-3100/B, 319-3110", basic: "PS-1000", premier: "PS-3865R" },
        { model: "SMF, SVL56 High Flow", mfgNo: "319-3100/B, 319-3110", basic: "PS-1000", premier: "PS-3865R" },
        { model: "Spa Flo, Spa Flo II", mfgNo: "319-3010/B 319-3090/B", basic: "none", premier: "PS-3867" },
        { model: "Viper, Supreme", mfgNo: "319-3010/B 319-3090/B", basic: "none", premier: "PS-3867" }
      ],
      "Wayne Water Systems": [
        { model: "Manufacturer Part Number 15693", mfgNo: "15693", basic: "PS-201", premier: "PS-3868" }
      ],
      "Zodiac Pool Systems": [
        { model: "Manufacturer Part Number A0580501 & A0580502", mfgNo: "A0580501 & A0580502", basic: "PS-2733", premier: "PS-3868" }
      ]
    };
    // Shopify handle per seal SKU (resolved 2026-11-02). A seal with no entry
    // here, or whose product isn't published, shows "not sold online": PS-2131,
    // PS-2733, PS-3866, PS-3869, PS-3960, PS-3986, PS-4231, PS-4577 and PS-961
    // have no Shopify product; PS-360 and PS-601 are drafts. PS-100 went active
    // 2026-11-02.
    var SEAL_HANDLES = {
      "PS-100": "usseal_ps-100",
      "PS-1000": "us-seal-pump-shaft-seal-5-8-ps-1000",
      "PS-200": "us_seal_mfg_ps-200",
      "PS-201": "us-seal-pump-shaft-seal-3-4-ps-201",
      "PS-201V-CMS": "us-seal-ps-201v-cms-ozone-pump-shaft-seal-premium-viton-construction-3-4-shaft",
      "PS-3865R": "us-seal-5-8-pump-shaft-seal-ps-3865r",
      "PS-3867": "u-s-seal-salt-ozone-5-8-pool-pump-shaft-seal-ps-3867",
      "PS-3868": "u-s-seal-salt-ozone-3-4-pool-pump-shaft-seal-ps-3868",
      "PS-3890": "us-seal-pump-shaft-seal-5-8-ps-3890",
      "PS-4280": "us-seal-pump-shaft-seal-premium-viton-5-8-ps-4280",
      "PS-601": "ussealmanufacturing_ps-601"
    };
    // Material facts from the PS-201 (Basic) and PS-3868 (Premier) product
    // descriptions on poolaroo.com, checked 2026-11-02. Use lines follow U.S.
    // Seal's printed grade text (poolaroo-writer, style_guide_us_seal_shaft_seals
    // v0.3): Basic "Generally reliable in residential chlorine pools"; Premier
    // "Great for any service whether ozonators, salt chlorine generators...".
    // Never use U.S. Seal's popularity, superlative or reliability lines.
    var SEAL_TYPES = {
      basic: { name: 'Basic Service seal', desc: 'Buna elastomers, molded plastic primary ring and ceramic seat. For residential chlorine pools.' },
      premier: { name: 'Premier Viton seal', desc: 'Viton elastomers, carbon primary ring and polished ceramic seat. Made for salt chlorine generators and ozone systems.' }
    };
    // Disputed fits (guide §9, W33): a model + seal pair listed here answers
    // "We can't confirm" instead of "Fits". Key format: 'Model|PS-XXX'.
    // Empty since 2026-11-02, when Dieter ruled that Sta-Rite 37400-0028S
    // pumps take no Basic seal (the Sta-Rite bulletin wins over the guide).
    var DISPUTED = {};
    // A seal of "none" shows as an "N/A" card beside the other option
    // (Dieter, 2026-11-02, for every pump with no Basic seal).
    function naHtml(type) {
      return '<p class="pw-option__name">' + PW.esc(SEAL_TYPES[type].name) + '</p>' +
        '<p class="pw-option__sku">N/A</p>';
    }
    function cantConfirmHtml(type, sku) {
      return '<p class="pw-option__name">' + PW.esc(SEAL_TYPES[type].name) + '</p>' +
        '<p class="pw-option__sku">' + PW.esc(sku) + '</p>' +
        '<p class="pw-status">We can\'t confirm this seal fits your pump. ' + PW.contactLink() + ' and we\'ll check it for you.</p>';
    }

    mount.innerHTML = PW.card('seal', 'Find the shaft seal that fits your pump',
      'Choose your pump\'s manufacturer and model.',
      '<form class="pw-form" novalidate>' +
        '<div class="pw-field"><label class="pw-label" for="pw-seal-mfg">Pump manufacturer</label>' +
          '<select class="pw-select" id="pw-seal-mfg"><option value="">Choose a manufacturer</option>' +
          Object.keys(sealData).map(function(m) { return '<option value="' + PW.esc(m) + '">' + PW.esc(m) + '</option>'; }).join('') +
          '</select></div>' +
        '<div class="pw-field"><label class="pw-label" for="pw-seal-model">Pump model</label>' +
          '<select class="pw-select" id="pw-seal-model" disabled><option value="">Choose a model</option></select></div>' +
      '</form>' +
      '<div class="pw-result" id="pw-seal-result" aria-live="polite" hidden></div>' +
      '<p class="pw-note">Not sure which pump you have? ' + PW.contactLink() + ' and we\'ll check it for you.</p>');

    var root = mount.querySelector('.pw-widget');
    var mfgSelect = root.querySelector('#pw-seal-mfg');
    var modelSelect = root.querySelector('#pw-seal-model');
    var result = root.querySelector('#pw-seal-result');
    var models = [];

    root.querySelector('form').addEventListener('submit', function(e) { e.preventDefault(); });

    mfgSelect.addEventListener('change', function() {
      models = sealData[mfgSelect.value] || [];
      modelSelect.innerHTML = '<option value="">Choose a model</option>' +
        models.map(function(m, i) { return '<option value="' + i + '">' + PW.esc(m.model) + '</option>'; }).join('');
      modelSelect.disabled = !models.length;
      result.hidden = true;
      if (mfgSelect.value) PW.track(TOOL, 'Mfg_Select', mfgSelect.value);
    });

    modelSelect.addEventListener('change', function() {
      var m = models[modelSelect.value];
      if (!m) { result.hidden = true; return; }
      PW.track(TOOL, 'Model_Select', m.model);
      var types = ['basic', 'premier'].filter(function(t) { return m[t] !== 'none'; });
      var disputed = types.filter(function(t) { return DISPUTED[m.model + '|' + m[t]]; });
      var title = '<p class="pw-result__value pw-result__value--text">' + PW.esc(mfgSelect.value) + ' ' + PW.esc(m.model) + '</p>';
      // Rows the guide gives no model name for are labeled "Manufacturer Part
      // Number <mfgNo>" (Dieter, 2026-11-02); skip the line that would repeat it.
      var parts = m.mfgNo && m.model.indexOf(m.mfgNo) === -1 ? 'Original seal part ' + (m.mfgNo.indexOf(',') === -1 ? 'number' : 'numbers') + ': ' + PW.esc(m.mfgNo) + '. ' : '';
      var slots = ['basic', 'premier'];
      result.hidden = false;
      if (disputed.length === types.length) {
        // Guide §8: three outcomes only. No add buttons on "We can't confirm".
        result.className = 'pw-result pw-result--warning';
        result.innerHTML = '<p class="pw-result__label">We can\'t confirm</p>' + title +
          '<p class="pw-result__detail">' + parts + 'We can\'t confirm which shaft seal fits this pump yet. ' +
          PW.contactLink() + ' and we\'ll check it for you.</p>';
        return;
      }
      result.className = 'pw-result pw-result--success';
      result.innerHTML = '<p class="pw-result__label">Fits</p>' + title +
        '<p class="pw-result__detail">' + parts + (disputed.length || types.length === 1 ? 'The U.S. Seal option below replaces the original seal.' : 'Either U.S. Seal option below replaces the original seal.') + '</p>' +
        '<div class="pw-options' + (slots.length === 2 ? ' pw-options--2' : '') + '">' +
          slots.map(function(t) { return '<div class="pw-option" data-type="' + t + '"></div>'; }).join('') + '</div>';
      slots.forEach(function(type) {
        var sku = m[type];
        var slot = result.querySelector('[data-type="' + type + '"]');
        if (sku === 'none') { slot.innerHTML = naHtml(type); return; }
        if (disputed.indexOf(type) !== -1) { slot.innerHTML = cantConfirmHtml(type, sku); return; }
        PW.renderProductOption(slot, {
          name: SEAL_TYPES[type].name, sku: sku, desc: SEAL_TYPES[type].desc, handle: SEAL_HANDLES[sku],
          buttonLabel: 'Add 1 ' + sku + ' seal to cart', successText: 'Added 1 ' + sku + ' seal to your cart.'
        }, TOOL);
      });
    });
  });

  // ==========================================
  // WIDGET: Unicel Cartridge Finder
  // Mount: #unicel-compatibility-widget (not on any page yet; the fit data is
  // incomplete and will be redone before it's published)
  // Rebuilt to poolaroo_web_widgets v0.2 on 2026-11-02: add to cart replaces
  // the product link.
  // ==========================================
  PW.mount('unicel-compatibility-widget', function(mount) {
    var TOOL = 'Unicel_Compatibility';
    // Hardcoded fit list: a known deviation from guide §9 (W32), and
    // incomplete; it gets redone before this widget is published. Source:
    // carried over from the original widget, which credits Unicel's
    // compatibility database (not verified). Last checked: not checked (as of
    // 2026-11-02). Known oddity: "Easy Clear C400" points at a product listed
    // as a CX481XRE replacement.
    var compatibilityData = {
      "Hayward": {
        "Star-Clear C1000": { oem_part: "CX1000RE", unicel_sku: "C-7470", shopify_handle: "unicel-filter-cartridge-80-sf-clean-clear-c-7470", specs: { filter_area: 75, length: "20\"", diameter: "7\"", pleats: 170 } },
        "Star-Clear Plus C1200": { oem_part: "CX1200RE", unicel_sku: "C-8412", shopify_handle: "unicel-filter-cartridge-120-sf-cx1200-re-waterway-pro-clean-c-8412", specs: { filter_area: 120, length: "23.3\"", diameter: "8.9\"", pleats: 194 } },
        "Easy Clear C400": { oem_part: "CX410RE", unicel_sku: "C-7456", shopify_handle: "unicel-56-sq-ft-hayward-cx481xre-rep-c-7456", specs: { filter_area: 40, length: "13.5\"", diameter: "7\"", pleats: 146 } }
      },
      "Pentair": {
        "Clean & Clear Plus (160340)": { oem_part: "160340", unicel_sku: "C-7470", shopify_handle: "unicel-filter-cartridge-80-sf-clean-clear-c-7470", specs: { filter_area: 75, length: "20\"", diameter: "7\"", pleats: 170 } },
        "Clean & Clear Plus (160317)": { oem_part: "160317", unicel_sku: "C-7471", shopify_handle: "unicel-filter-cartridge-105-sf-clean-clear-c-7471", specs: { filter_area: 105, length: "26\"", diameter: "7\"", pleats: 168 } },
        "Predator 100": { oem_part: "160332", unicel_sku: "C-9699", shopify_handle: "unicel-filter-cartridge-100-sf-jacuzzi-cfr-100-c-9699", specs: { filter_area: 100, length: "19.9\"", diameter: "9.9\"", pleats: 194 } }
      },
      "Waterway": {
        "Crystal Water 50": { oem_part: "817-0050", unicel_sku: "C-4950", shopify_handle: "unicel-filter-cartridge-50-sf-rainbow-in-line-c-4950", specs: { filter_area: 50, length: "13.3\"", diameter: "4.9\"", pleats: 212 } },
        "Top Load 40": { oem_part: "817-0040", unicel_sku: "4CH-940", shopify_handle: "unicel-filter-cartridge-40-sf-4ch-940", specs: { filter_area: 40, length: "15\"", diameter: "4.6\"", pleats: 176 } }
      },
      "Sta-Rite": {
        "System 3 Modular (S7M120)": { oem_part: "25021-0200", unicel_sku: "UHD-SR100", shopify_handle: "unicel-filter-cartridge-102-sf-sta-rite-uhd-sr100", specs: { filter_area: 102, length: "25.7\"", diameter: "8.7\"", pleats: 195 } }
      },
      "Jacuzzi": {
        "CFR-100": { oem_part: "42-3598-08-R", unicel_sku: "C-9699", shopify_handle: "unicel-filter-cartridge-100-sf-jacuzzi-cfr-100-c-9699", specs: { filter_area: 100, length: "19.9\"", diameter: "9.9\"", pleats: 194 } }
      }
    };
    function inches(s) { return String(s).replace(/"$/, ' in'); }

    mount.innerHTML = PW.card('unicel', 'Find the Unicel cartridge that fits your filter',
      'Choose your filter\'s manufacturer and model.',
      '<form class="pw-form" novalidate>' +
        '<div class="pw-field"><label class="pw-label" for="pw-unicel-mfg">Filter manufacturer</label>' +
          '<select class="pw-select" id="pw-unicel-mfg"><option value="">Choose a manufacturer</option>' +
          Object.keys(compatibilityData).map(function(m) { return '<option value="' + PW.esc(m) + '">' + PW.esc(m) + '</option>'; }).join('') +
          '</select></div>' +
        '<div class="pw-field"><label class="pw-label" for="pw-unicel-model">Filter model</label>' +
          '<select class="pw-select" id="pw-unicel-model" disabled><option value="">Choose a model</option></select></div>' +
      '</form>' +
      '<div class="pw-result pw-result--success" id="pw-unicel-result" aria-live="polite" hidden></div>' +
      '<p class="pw-note">Not sure which filter you have? ' + PW.contactLink() + ' and we\'ll check it for you.</p>');

    var root = mount.querySelector('.pw-widget');
    var mfgSelect = root.querySelector('#pw-unicel-mfg');
    var modelSelect = root.querySelector('#pw-unicel-model');
    var result = root.querySelector('#pw-unicel-result');

    root.querySelector('form').addEventListener('submit', function(e) { e.preventDefault(); });

    mfgSelect.addEventListener('change', function() {
      var models = compatibilityData[mfgSelect.value] || {};
      modelSelect.innerHTML = '<option value="">Choose a model</option>' +
        Object.keys(models).map(function(m) { return '<option value="' + PW.esc(m) + '">' + PW.esc(m) + '</option>'; }).join('');
      modelSelect.disabled = !mfgSelect.value;
      result.hidden = true;
      if (mfgSelect.value) PW.track(TOOL, 'Mfg_Select', mfgSelect.value);
    });

    modelSelect.addEventListener('change', function() {
      var data = (compatibilityData[mfgSelect.value] || {})[modelSelect.value];
      if (!data) { result.hidden = true; return; }
      PW.track(TOOL, 'Model_Select', modelSelect.value);
      var s = data.specs;
      result.innerHTML = '<p class="pw-result__label">Fits</p>' +
        '<p class="pw-result__value pw-result__value--text">Unicel ' + PW.esc(data.unicel_sku) + '</p>' +
        '<p class="pw-result__detail">Replaces ' + PW.esc(data.oem_part) + ' in the ' + PW.esc(mfgSelect.value) + ' ' + PW.esc(modelSelect.value) + '.</p>' +
        '<div class="pw-table-scroll"><table class="pw-table"><caption class="pw-visually-hidden">Unicel ' + PW.esc(data.unicel_sku) + ' sizes</caption><tbody>' +
          '<tr><th scope="row">Filter area</th><td class="pw-num">' + s.filter_area + ' sq ft</td></tr>' +
          '<tr><th scope="row">Length</th><td class="pw-num">' + PW.esc(inches(s.length)) + '</td></tr>' +
          '<tr><th scope="row">Diameter</th><td class="pw-num">' + PW.esc(inches(s.diameter)) + '</td></tr>' +
          '<tr><th scope="row">Pleats</th><td class="pw-num">' + s.pleats + '</td></tr>' +
        '</tbody></table></div>' +
        '<p class="pw-result__detail">Measure your old cartridge before you order. Its length and diameter should match the sizes above.</p>' +
        '<div class="pw-options"><div class="pw-option" data-slot="cartridge"></div></div>';
      result.hidden = false;
      PW.renderProductOption(result.querySelector('[data-slot="cartridge"]'), {
        name: 'Unicel replacement cartridge', sku: data.unicel_sku, handle: data.shopify_handle,
        buttonLabel: 'Add 1 Unicel ' + data.unicel_sku + ' cartridge to cart',
        successText: 'Added 1 Unicel ' + data.unicel_sku + ' cartridge to your cart.'
      }, TOOL);
    });
  });

  // ==========================================
  // WIDGET: Vinyl Works Fence Sizer
  // Mount: #vinyl-fence-calc-target (live on 6 Vinyl Works kit products; keep the id)
  // Rebuilt to poolaroo_web_widgets v0.2 on 2026-11-02: one button adds the
  // whole kit set to the cart in a single call, replacing three product links.
  //
  // Kits: A = 8-section base (every fence starts with one), B = 3-section
  // add-on, C = 2-section add-on. Source for the sections per kit and the
  // 64-1/2 inch section length: the Vinyl Works kit product titles and
  // descriptions on poolaroo.com (checked 2026-11-02). Logic carried over unchanged from the
  // original sizer: after kit A, cover the rest with B kits, using one C for a
  // remainder of 1 or 2 and two Cs for a remainder of 1 after the Bs.
  //
  // Test cases (checked by hand):
  //   6 sections  -> A                 (8, 2 extra)
  //   12 sections -> A + 2C            (12)
  //   20 sections -> A + 4B            (20)
  //   21 sections -> A + 3B + 2C       (21)
  // ==========================================
  PW.mount('vinyl-fence-calc-target', function(mount) {
    var TOOL = 'Vinyl Works Fence';
    var KITS = {
      white: {
        a: 'vinyl-works-premium-24-fence-kit-a-8-section-base-white-a24p8-w',
        b: 'vinyl-works-premium-24-fence-kit-b-3-section-add-on-white-b24p3-w',
        c: 'vinyl_works_premium_2_section_fence_addon_white_c24p2-w'
      },
      taupe: {
        a: 'vinyl-works-premium-24-8-section-base-fence-kit-a-taupe-a24p8-t',
        b: 'vinyl-works-premium-24-3-section-fence-add-on-kit-b-taupe-b24p3-t',
        c: 'vinyl-works-premium-24-2-section-fence-add-on-kit-c-taupe-c24p2-t'
      }
    };
    var KIT_INFO = {
      a: { name: 'Base Kit A', sections: 8 },
      b: { name: 'Add-On Kit B', sections: 3 },
      c: { name: 'Add-On Kit C', sections: 2 }
    };
    var SECTIONS = { id: 'pw-fence-sections', label: 'Sections to cover', min: 1, max: 60, integer: true,
      help: 'Count the top rails you want to cover between the pool\'s support posts. Each section spans up to 64.5 inches. Leave out any sections where the pool meets a deck.' };

    mount.innerHTML = PW.card('fence', 'How many fence kits do you need?',
      'Choose a color and count the open sections around your pool.',
      '<form class="pw-form" novalidate>' +
        PW.choiceGroup('pw-fence-color', 'Fence color', [{ value: 'white', label: 'White' }, { value: 'taupe', label: 'Taupe' }]) +
        PW.numberField(SECTIONS) +
      '</form>' +
      '<div class="pw-result" id="pw-fence-result" aria-live="polite" hidden></div>');

    var root = mount.querySelector('.pw-widget');
    var input = root.querySelector('#pw-fence-sections');
    var result = root.querySelector('#pw-fence-result');
    var analyticsTimer;
    var renderSeq = 0;

    root.querySelector('form').addEventListener('submit', function(e) { e.preventDefault(); });

    function kitsFor(sections) {
      var k = { a: 1, b: 0, c: 0 };
      var rem = sections - 8;
      if (rem === 1 || rem === 2) k.c = 1;
      else if (rem >= 3) {
        if (rem % 3 === 0) k.b = rem / 3;
        else if (rem % 3 === 1) { k.b = (rem - 4) / 3; k.c = 2; }
        else { k.b = (rem - 2) / 3; k.c = 1; }
      }
      return k;
    }

    function update() {
      var color = PW.choiceValue(root, 'pw-fence-color');
      var r = PW.readNumber(input, SECTIONS);
      clearTimeout(analyticsTimer);
      if (!color || r.value === undefined) { result.hidden = true; return; }

      var sections = r.value;
      var k = kitsFor(sections);
      var keys = ['a', 'b', 'c'].filter(function(key) { return k[key] > 0; });
      var kitCount = keys.reduce(function(s, key) { return s + k[key]; }, 0);
      var covered = keys.reduce(function(s, key) { return s + k[key] * KIT_INFO[key].sections; }, 0);
      var seq = ++renderSeq;

      result.innerHTML = '<p class="pw-result__label">You need</p>' +
        '<p class="pw-result__value">' + kitCount + ' <span class="pw-result__unit">' + PW.plural(kitCount, 'kit', 'kits') + ' in ' + color + '</span></p>' +
        '<div class="pw-table-scroll" tabindex="0" role="region" aria-label="Fence kits to order"><table class="pw-table">' +
          '<caption class="pw-visually-hidden">Fence kits to order</caption>' +
          '<thead><tr><th scope="col">Kit</th><th scope="col" class="pw-num">Sections each</th><th scope="col" class="pw-num">Quantity</th></tr></thead><tbody>' +
          keys.map(function(key) {
            // Kit names link to their product pages (Dieter, 2026-11-02).
            var kitName = PW.isCurrentProduct(KITS[color][key]) ? PW.esc(KIT_INFO[key].name) : PW.productLink(KITS[color][key], KIT_INFO[key].name);
            return '<tr><th scope="row">' + kitName + '</th><td class="pw-num">' + KIT_INFO[key].sections + '</td><td class="pw-num">' + k[key] + '</td></tr>';
          }).join('') +
        '</tbody></table></div>' +
        '<p class="pw-result__detail">That covers ' + covered + ' sections' +
          (covered > sections ? ', so you\'ll have ' + (covered - sections) + ' extra ' + PW.plural(covered - sections, 'section', 'sections') + '.' : ', exactly what you need.') + '</p>' +
        '<div class="pw-result__next" aria-busy="true"><p class="pw-status">Checking stock...</p></div>';
      result.hidden = false;
      analyticsTimer = setTimeout(function() { PW.track(TOOL, 'Calculated', sections); }, 2000);

      var next = result.querySelector('.pw-result__next');
      Promise.all(keys.map(function(key) { return PW.getProduct(KITS[color][key]); })).then(function(products) {
        if (seq !== renderSeq) return;
        next.removeAttribute('aria-busy');
        var missing = [];
        var total = 0;
        var items = keys.map(function(key, i) {
          var v = PW.firstVariant(products[i]);
          if (!v || !v.available) missing.push(key);
          else total += v.price * k[key];
          return v ? { id: v.id, quantity: k[key] } : null;
        });
        var pageLinks = keys.map(function(key) { return PW.productLink(KITS[color][key], KIT_INFO[key].name + ' product page'); }).join(', ');
        if (missing.length) {
          // Guide §4.10: no button when part of the set is out of stock; the
          // set is added in full or not at all.
          next.innerHTML = '<p class="pw-status">' + missing.map(function(key) { return KIT_INFO[key].name; }).join(' and ') +
            ' in ' + color + ' ' + PW.plural(missing.length, 'is', 'are') + ' out of stock right now. See the ' +
            missing.map(function(key) { return PW.productLink(KITS[color][key], KIT_INFO[key].name + ' product page'); }).join(' and ') +
            ' for details.</p>';
          return;
        }
        var only = keys.length === 1 ? keys[0] : null;
        var addLabel = only
          ? 'Add ' + k[only] + ' ' + KIT_INFO[only].name + ' to cart'
          : 'Add all ' + kitCount + ' to cart';
        next.innerHTML = '<p class="pw-option__price">Total: ' + PW.money(total) + '</p>' +
          '<button type="button" class="btn pw-btn">' + addLabel + '</button>' +
          '<p class="pw-status" role="status" aria-live="polite"></p>';
        var btn = next.querySelector('button');
        btn.addEventListener('click', function() {
          PW.addToCart(items, btn, next.querySelector('[role="status"]'), {
            tool: TOOL, pageLinks: pageLinks,
            successText: 'Added ' + kitCount + ' ' + color + ' fence ' + PW.plural(kitCount, 'kit', 'kits') + ' to your cart.'
          });
        });
      }).catch(function() {
        if (seq !== renderSeq) return;
        next.removeAttribute('aria-busy');
        next.innerHTML = '<p class="pw-status pw-status--error">Error: We couldn\'t check stock. Refresh the page, or ' +
          PW.contactLink().replace('Contact', 'contact') + ' and we\'ll help with your order.</p>';
      });
    }

    PW.wireChoice(root, 'pw-fence-color', update);
    PW.wireNumber(input, SECTIONS, update);
  });

  // ==========================================
  // WIDGET: Gladon Pool Cove Calculator
  // Mount: <div id="cove-calculator" class="pw-mount"></div>
  //   on GLADON PNP192XL (https://poolaroo.com/products/gladon_pnp192xl)
  // Built 2026-11-02 to poolaroo_web_widgets v0.2.
  //
  // Each piece is 48 inches (4 ft) long and sold individually.
  //   Round: perimeter = pi x diameter
  //   Oval (two half-circle ends joined by straight sides):
  //          perimeter = pi x width + 2 x (length - width)
  //   Pieces = ceil(perimeter / 4)
  // Chart rule (guide §9, W30): the manufacturer's chart wins at every size it
  // lists; where only third-party charts exist and disagree, the higher count
  // wins; the formula covers every other size.
  //   Round: Gladon's chart (via clubpiscine.ca, checked 2026-11-02). It
  //     matches the formula at all 10 sizes. Inyo lists 14 for 18 ft (Gladon
  //     15); Gladon wins.
  //   Oval: no Gladon oval chart found (searched 2026-11-02). Inyo Pools'
  //     chart matches the formula at 9 of 12 sizes. Disagreements:
  //       12x16: formula 12, Inyo 13 -> 13
  //       15x24: formula 17, Inyo 16 -> 17
  //       15x30: formula 20, Inyo 19, The Pool Factory's Gladon XL kit 20
  //              (an Inyo buyer also reported 19 fell short) -> 20
  //
  // Test cases (checked by hand):
  //   Round 12 ft      -> 37.70 ft  -> 10 pieces (chart 10)
  //   Round 24 ft      -> 75.40 ft  -> 19 pieces (chart 19)
  //   Round 26.5 ft    -> 83.25 ft  -> 21 pieces (formula; not a chart size)
  //   Oval 12 x 16 ft  -> 45.70 ft  -> 13 pieces (chart beats formula's 12)
  //   Oval 15 x 30 ft  -> 77.12 ft  -> 20 pieces
  //   Oval 18 x 33 ft  -> 86.55 ft  -> 22 pieces
  // ==========================================
  PW.mount('cove-calculator', function(mount) {
    var TOOL = 'Gladon Cove';
    var HANDLE = 'gladon_pnp192xl';
    var PIECE_FT = 4;
    var ROUND_CHART = { 12: 10, 15: 12, 16: 13, 18: 15, 20: 16, 21: 17, 24: 19, 27: 22, 30: 24, 33: 26 };
    var OVAL_CHART = { '12x16': 13, '15x24': 17, '15x30': 20 };
    var DIAMETER = { id: 'pw-cove-diameter', label: 'Pool diameter (feet)', min: 8, max: 40,
      help: 'Measure straight across the pool, wall to wall, for example 24.' };
    var WIDTH = { id: 'pw-cove-width', label: 'Pool width (feet)', min: 8, max: 25, short: false,
      help: 'The shortest distance across, wall to wall, for example 15.' };
    var LENGTH = { id: 'pw-cove-length', label: 'Pool length (feet)', min: 10, max: 50, short: false,
      help: 'The longest distance across, wall to wall, for example 30.' };

    mount.innerHTML = PW.card('cove', 'How many cove pieces does your pool need?',
      'Choose your pool\'s shape and enter its size in feet.',
      '<form class="pw-form" novalidate>' +
        PW.choiceGroup('pw-cove-shape', 'Pool shape', [{ value: 'round', label: 'Round' }, { value: 'oval', label: 'Oval' }]) +
        '<div id="pw-cove-round" hidden>' + PW.numberField(DIAMETER) + '</div>' +
        '<div id="pw-cove-oval" class="pw-row" hidden>' + PW.numberField(WIDTH) + PW.numberField(LENGTH) + '</div>' +
      '</form>' +
      '<div class="pw-result" id="pw-cove-result" aria-live="polite" hidden></div>' +
      '<p class="pw-note">Estimate based on 48-inch pieces. Published charts differ by one piece for some oval sizes, so we use the higher count.</p>');

    var root = mount.querySelector('.pw-widget');
    var roundWrap = root.querySelector('#pw-cove-round');
    var ovalWrap = root.querySelector('#pw-cove-oval');
    var dInput = root.querySelector('#pw-cove-diameter');
    var wInput = root.querySelector('#pw-cove-width');
    var lInput = root.querySelector('#pw-cove-length');
    var result = root.querySelector('#pw-cove-result');
    var analyticsTimer;
    var renderSeq = 0;

    root.querySelector('form').addEventListener('submit', function(e) { e.preventDefault(); });

    function update() {
      var shape = PW.choiceValue(root, 'pw-cove-shape');
      roundWrap.hidden = shape !== 'round';
      ovalWrap.hidden = shape !== 'oval';
      clearTimeout(analyticsTimer);

      var perimeter, sizeText, chartPieces;
      if (shape === 'round') {
        var d = PW.readNumber(dInput, DIAMETER);
        if (d.value === undefined) { result.hidden = true; return; }
        perimeter = Math.PI * d.value;
        sizeText = 'A ' + PW.fmt(d.value, 1) + ' ft round pool';
        chartPieces = ROUND_CHART[d.value];
      } else if (shape === 'oval') {
        var w = PW.readNumber(wInput, WIDTH);
        var l = PW.readNumber(lInput, LENGTH);
        if (w.value === undefined || l.value === undefined) { result.hidden = true; return; }
        if (l.value <= w.value) {
          result.hidden = true;
          PW.setError(lInput, 'Length must be longer than width. If they\'re the same, choose Round.');
          return;
        }
        PW.setError(lInput, '');
        perimeter = Math.PI * w.value + 2 * (l.value - w.value);
        sizeText = 'A ' + PW.fmt(w.value, 1) + ' by ' + PW.fmt(l.value, 1) + ' ft oval pool';
        chartPieces = OVAL_CHART[w.value + 'x' + l.value];
      } else {
        result.hidden = true;
        return;
      }

      var formulaPieces = Math.ceil(perimeter / PIECE_FT);
      var pieces = chartPieces || formulaPieces;
      var basis = sizeText + ' measures about ' + PW.fmt(perimeter, 1) + ' ft around the wall. ' +
        (pieces === formulaPieces
          ? 'Each piece covers 4 ft, so we rounded up to ' + pieces + ' ' + PW.plural(pieces, 'piece', 'pieces') + '.'
          : 'Each piece covers 4 ft, which gives ' + formulaPieces + ', but published cove charts list ' + pieces + ' for this size, so we use ' + pieces + '.');
      var seq = ++renderSeq;
      result.innerHTML = '<p class="pw-result__label">You need</p>' +
        '<p class="pw-result__value">' + pieces + ' <span class="pw-result__unit">' + PW.plural(pieces, 'piece', 'pieces') + ' of cove</span></p>' +
        '<p class="pw-result__detail">' + basis + '</p>' +
        '<div class="pw-result__next" aria-busy="true"><p class="pw-status">Checking stock...</p></div>';
      result.hidden = false;
      analyticsTimer = setTimeout(function() { PW.track(TOOL, shape, pieces); }, 2000);

      var next = result.querySelector('.pw-result__next');
      PW.getProduct(HANDLE).then(function(product) {
        if (seq !== renderSeq) return;
        next.removeAttribute('aria-busy');
        var v = PW.firstVariant(product);
        if (!v || !v.available) {
          next.innerHTML = PW.outOfStockHtml('Gladon PNP192XL cove', HANDLE);
          return;
        }
        var label = pieces + ' ' + PW.plural(pieces, 'piece', 'pieces') + ' of cove';
        next.innerHTML = '<button type="button" class="btn pw-btn">Add ' + label + ' to cart</button>' +
          '<p class="pw-status" role="status" aria-live="polite"></p>';
        var btn = next.querySelector('button');
        btn.addEventListener('click', function() {
          PW.addToCart([{ id: v.id, quantity: pieces }], btn, next.querySelector('[role="status"]'), {
            tool: TOOL, successText: 'Added ' + label + ' to your cart.',
            pageLinks: PW.productLink(HANDLE, 'Gladon PNP192XL product page')
          });
        });
      }).catch(function() {
        if (seq !== renderSeq) return;
        next.removeAttribute('aria-busy');
        next.innerHTML = '<p class="pw-status pw-status--error">Error: We couldn\'t check stock. Refresh the page, or ' +
          PW.contactLink().replace('Contact', 'contact') + ' and we\'ll help with your order.</p>';
      });
    }

    PW.wireChoice(root, 'pw-cove-shape', update);
    PW.wireNumber(dInput, DIAMETER, update);
    PW.wireNumber(wInput, WIDTH, update);
    PW.wireNumber(lInput, LENGTH, update);
  });

  // ==========================================
  // GLOBAL HELPERS (kept for anything that still calls them by name)
  // ==========================================

  // Legacy entry point: the pre-2026-11-02 Fiber Calculator called this from
  // inline onclick handlers. Kept so any cached or pasted markup still works.
  window.addBundleToCart = function(itemsArray, btnElement) {
    var status = document.createElement('p');
    status.className = 'pw-status';
    status.setAttribute('role', 'status');
    btnElement.insertAdjacentElement('afterend', status);
    PW.addToCart(itemsArray, btnElement, status);
  };
})();

// GA4 analytics helper the widgets call. custom-article-widgets.js carries a
// copy (without the console logging) for article pages: change one, change both.
window.sendAnalyticsEvent = function(toolName, filterType, inputValue) {
  // Check if gtag exists (Standard Shopify setup)
  if (typeof gtag === 'function') {
    gtag('event', 'use_calculator', {
      'tool_name': toolName,
      'filter_type': filterType,
      'input_value': inputValue
    });
    console.log("GA4 Event Sent: " + toolName + " | " + filterType);
  }
  // Fallback: Check if dataLayer exists (GTM setup)
  else if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'use_calculator',
      'tool_name': toolName,
      'filter_type': filterType,
      'input_value': inputValue
    });
    console.log("DataLayer Event Pushed: " + toolName + " | " + filterType);
  }
};
